import React from "react";
import "../assets/css/skeleton.css";

/** Base shimmer block. Everything else composes this. */
export const Skeleton = ({ w = "100%", h = 14, r = 8, className = "", style }) => (
  <span
    className={`sk-skel ${className}`}
    style={{ width: w, height: h, borderRadius: r, ...style }}
  />
);

/** Data-grid placeholder: a header row plus `rows` × `cols` cells. */
export const TableSkeleton = ({ rows = 8, cols = 6 }) => (
  <div className="sk-table" style={{ "--sk-cols": cols }} aria-hidden="true">
    <div className="sk-table__head">
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={i} h={11} w={i === 0 ? "55%" : "68%"} />
      ))}
    </div>

    {Array.from({ length: rows }).map((_, row) => (
      <div className="sk-table__row" key={row}>
        {Array.from({ length: cols }).map((_, col) => (
          <Skeleton
            key={col}
            h={12}
            // Vary widths a little so it reads as text, not a grid of bars.
            w={col === 0 ? "45%" : `${55 + ((row + col) % 4) * 11}%`}
          />
        ))}
      </div>
    ))}
  </div>
);

/** Stat-card row placeholder. */
export const CardsSkeleton = ({ count = 4 }) => (
  <div className="sk-cards" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <div className="sk-card" key={i}>
        <Skeleton w={36} h={36} r={999} />
        <Skeleton w="42%" h={24} />
        <Skeleton w="62%" h={12} />
        <Skeleton w="50%" h={10} />
      </div>
    ))}
  </div>
);

/** Chart placeholder: legend chips above a set of bars. */
export const ChartSkeleton = ({ height = 240 }) => (
  <div className="sk-chart" style={{ height }} aria-hidden="true">
    <div className="sk-chart__legend">
      <Skeleton w={68} h={10} />
      <Skeleton w={68} h={10} />
      <Skeleton w={68} h={10} />
    </div>
    <div className="sk-chart__bars">
      {[62, 88, 45, 74, 95, 58, 80].map((h, i) => (
        <Skeleton key={i} w="100%" h={`${h}%`} r={6} />
      ))}
    </div>
  </div>
);

/** Avatar + name + stat row, for profile-style cards. */
export const ProfileSkeleton = () => (
  <div className="sk-profile" aria-hidden="true">
    <Skeleton w={96} h={96} r={999} />
    <Skeleton w={130} h={16} />
    <Skeleton w={100} h={11} />
    <div className="sk-profile__stats">
      <Skeleton w="100%" h={34} r={10} />
      <Skeleton w="100%" h={34} r={10} />
      <Skeleton w="100%" h={34} r={10} />
    </div>
  </div>
);

/**
 * Whole-page placeholder used by <Loader />.
 * `variant` picks the body shape; the title block is shared.
 */
export const PageSkeleton = ({ variant = "table", rows = 8, cols = 6 }) => {
  // Card-level placeholder — no page title.
  if (variant === "profile") {
    return (
      <div className="sk-page" role="status" aria-live="polite" aria-busy="true">
        <span className="sk-sr">Loading…</span>
        <ProfileSkeleton />
      </div>
    );
  }

  return (
  <div className="sk-page" role="status" aria-live="polite" aria-busy="true">
    <span className="sk-sr">Loading…</span>

    <div className="sk-page__title">
      <Skeleton w={220} h={22} />
      <Skeleton w={300} h={12} />
    </div>

    {variant === "dashboard" ? (
      <>
        <CardsSkeleton count={4} />
        <div className="sk-panel">
          <ChartSkeleton />
        </div>
      </>
    ) : variant === "cards" ? (
      <CardsSkeleton count={rows} />
    ) : variant === "chart" ? (
      <div className="sk-panel">
        <ChartSkeleton />
      </div>
    ) : (
      <>
        <div className="sk-page__toolbar">
          <Skeleton w={190} h={36} r={10} />
          <Skeleton w={160} h={36} r={10} />
          <Skeleton w={140} h={36} r={10} />
          <span className="sk-spacer" />
          <Skeleton w={110} h={36} r={10} />
        </div>
        <div className="sk-panel">
          <TableSkeleton rows={rows} cols={cols} />
        </div>
      </>
    )}
  </div>
  );
};

export default Skeleton;
